import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import AppleSignin from 'react-apple-signin-auth'

import Loader from '~/components/ui/Loader'
import CONFIG from '~/config'
import {
  APPLE_AUTH_TYPES,
  EXPERIMENTS,
} from '~/constants'
import useAppleAuthMethod from '~/hooks/useAppleAuthMethod'
import { useExperiment } from '~/hooks/useExperiment'
import { useSegmentEvents } from '~/hooks/useSegmentEvents'
import { ROUTES } from '~/pages/constants'
import {
  CONNECT_ACCOUNT_WITH_APPLE,
  MEMBER_REGISTERED,
  getSegmentUserContext,
  identifySegmentUser,
} from '~/services/segment'
import { useLazyFetchUserContextQuery } from '~/store/api'
import { setIsAuthorized } from '~/store/modules/auth'
import { setUserToken } from '~/utils'

import './styles.scss'

const SignInWithAppleButton = props => {
  const {
    promoCode,
    type,
    buttonClassName
  } = props

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const { authMethod, authMethodApiResult } = useAppleAuthMethod(type)
  const [fetchUserContext] = useLazyFetchUserContextQuery()

  const [
    trackMemberRegistration,
    trackConnectAccountWithApple,
  ] = useSegmentEvents([
    MEMBER_REGISTERED,
    CONNECT_ACCOUNT_WITH_APPLE,
  ])

  const { enabled: isPrimeGuaranteeEnabled } = useExperiment(EXPERIMENTS.PRIME_3_MONTH_GUARANTEE, false)

  const text = type === APPLE_AUTH_TYPES.SIGN_UP ? 'Sign up with Apple' : 'Sign in with Apple'

  const handleCredentialResponse = useCallback(async response => {
    setIsAuthenticating(true)
    const jwtToken = response?.authorization?.id_token
    const code = response?.authorization?.code
    const user = response?.user
    const apiResult = await authMethod({
      code,
      jwtToken,
      promoCode,
      user,
      withCreditScoreGuarantee: isPrimeGuaranteeEnabled
    })
    const { data, error } = apiResult

    setIsAuthenticating(false)

    if (!error) {
      if (data.action === APPLE_AUTH_TYPES.SIGN_UP) {
        trackMemberRegistration({ promoCode, loginType: 'apple' }, getSegmentUserContext(data.user))
      }
      setUserToken(data.jwtToken)
      dispatch(setIsAuthorized(true))
      identifySegmentUser(data.user)
      fetchUserContext()

      if (data.action === APPLE_AUTH_TYPES.SIGN_UP) {
        navigate(ROUTES.PRODUCT_TOURS.WELCOME)
      } else {
        navigate(ROUTES.DASHBOARD)
      }

      if (data.action === APPLE_AUTH_TYPES.CONNECT) {
        trackConnectAccountWithApple(getSegmentUserContext(data.user))
      }
    }
  }, [
    authMethod,
    dispatch,
    fetchUserContext,
    navigate,
    promoCode,
    trackConnectAccountWithApple,
    trackMemberRegistration,
    isPrimeGuaranteeEnabled,
  ])

  // redirectURI should be added to corresponding service (for "Apple sign in") in the Apple developer account
  const redirectURI = `${window.location.origin}/app/product-tours/welcome`

  return (
    <>
      {isAuthenticating ? (
        <Loader size="small" />
      ) : (
        <AppleSignin
          authOptions={{
            clientId: CONFIG.APPLE_OAUTH_CLIENT_ID,
            scope: 'email name',
            redirectURI,
            state: '',
            nonce: 'nonce',
            usePopup: true,
          }}
          onSuccess={handleCredentialResponse}
          data-testid="stellar-apple-oauth"
          noDefaultStyle
          uiType="light"
          className={`apple-btn h-10 w-[300px] relative flex justify-center mb-4 items-center bg-black text-white text-sm 
          rounded border-2 border-black overflow-hidden 
          ${buttonClassName}`}
          buttonExtraChildren={<span className="ml-9">{text}</span>}
        />
      )}
      {authMethodApiResult.error ? (
        <p className="text-red text-sm pb-4">
          {authMethodApiResult.error?.data?.errors?.[0]?.message}
        </p>
      ) : null}
    </>
  )
}

SignInWithAppleButton.propTypes = {
  promoCode: PropTypes.string,
  type: PropTypes.oneOf([
    APPLE_AUTH_TYPES.SIGN_UP,
    APPLE_AUTH_TYPES.SIGN_IN,
    APPLE_AUTH_TYPES.CONNECT,
  ]),
  buttonClassName: PropTypes.string,
}

SignInWithAppleButton.defaultProps = {
  promoCode: '',
  type: APPLE_AUTH_TYPES.SIGN_IN,
  buttonClassName: 'w-full',
}

export default SignInWithAppleButton
